/**
 * TermsPage.tsx
 *
 * TeachBud Terms of Service — effective date: 2026-08-03
 * IMPORTANT: This document is a draft for internal review and must be reviewed
 * by a qualified attorney before TeachBud opens to the general public.
 */

import React from 'react';
import { ArrowLeft, FileText } from 'lucide-react';

interface TermsPageProps {
  onBack?: () => void;
}

export function TermsPage({ onBack }: TermsPageProps) {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="h-8 w-8 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100
                flex items-center justify-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              aria-label="Go back"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-primary" />
            <span className="text-sm font-bold text-slate-900">TeachBud Terms of Service</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8 text-slate-700 text-sm leading-relaxed">

        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 text-amber-800 text-xs">
          <strong>Attorney Review Notice:</strong> These Terms of Service are a draft prepared for the private beta phase of TeachBud.
          They have not been reviewed by a qualified attorney. Do not rely on them as a complete or enforceable legal document
          until a formal legal review has been completed.
        </div>

        <div>
          <p className="text-xs text-slate-400 mb-2">Last updated: August 3, 2026 · Effective: August 3, 2026</p>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Terms of Service</h1>
          <p>
            These Terms of Service ("Terms") govern your access to and use of the TeachBud web application
            (the "Service") provided by TeachBud ("we," "us," or "our"). By creating an account or using
            the Service, you agree to these Terms. If you do not agree, do not use the Service.
          </p>
        </div>

        <Section title="1. Eligibility and Account Responsibilities">
          <p>
            The Service is intended for use by classroom teachers and school administrators who are adults (18 years
            or older) or who have authorization from their school or district. You are responsible for maintaining the
            confidentiality of your account credentials and for all activity that occurs under your account.
          </p>
          <p className="mt-2">
            You must provide accurate and complete information when creating your account. If we discover that you have
            provided false information, we may suspend or terminate your account.
          </p>
          <p className="mt-2">
            You are responsible for ensuring that your use of the Service complies with applicable laws and your
            school or district's policies, including any policies regarding the use of AI tools in education.
          </p>
        </Section>

        <Section title="2. Acceptable Use">
          <p>You agree to use the Service only for lawful purposes and in ways that do not harm TeachBud, other users, or third parties. You agree not to:</p>
          <ul className="list-disc list-inside space-y-1.5 ml-2 mt-2">
            <li>Upload or share content that is illegal, abusive, harassing, or violates any third party's rights.</li>
            <li>Upload documents containing personally identifiable student information (student names, ID numbers, grades, health information, or other student records).</li>
            <li>Use the Service to generate content that is misleading, discriminatory, or harmful to students.</li>
            <li>Attempt to reverse-engineer, scrape, or interfere with the operation of the Service.</li>
            <li>Share your account credentials with others or create accounts on behalf of others without authorization.</li>
            <li>Use the Service for any commercial purpose other than creating materials for your own classroom use, without our written permission.</li>
            <li>Upload copyrighted curriculum materials in violation of their license terms.</li>
          </ul>
          <p className="mt-2">
            Violation of these terms may result in immediate suspension or termination of your account.
          </p>
        </Section>

        <Section title="3. Ownership of Content">
          <SubSection title="3a. Your Content">
            <p>
              You retain ownership of the curriculum documents, lesson inputs, and other content you upload or create
              ("Your Content"). By uploading content to TeachBud, you grant us a limited, non-exclusive license to
              process and use Your Content solely to provide the Service to you. We do not claim ownership of Your Content.
            </p>
          </SubSection>
          <SubSection title="3b. AI-Generated Resources">
            <p>
              The lessons, activity sheets, assessments, and presentations generated by TeachBud ("Generated Resources")
              are created on your behalf using AI. You are free to use, modify, print, and distribute Generated Resources
              for your classroom and school purposes. We do not claim ownership of Generated Resources you create.
            </p>
            <p className="mt-2">
              We reserve the right to use anonymized, aggregated examples of Generated Resources (with no personally
              identifiable information) to improve our AI models and the quality of the Service.
            </p>
          </SubSection>
          <SubSection title="3c. TeachBud Intellectual Property">
            <p>
              The TeachBud application, its design, branding, algorithms, and all software components are owned by us
              and protected by copyright and other intellectual property laws. You may not copy, modify, or distribute
              our software or design.
            </p>
          </SubSection>
        </Section>

        <Section title="4. AI-Output Review Responsibility">
          <p>
            Generated Resources are produced by AI and may contain errors, inaccuracies, or content that is not
            appropriate for your students. You are solely responsible for reviewing all Generated Resources before
            using them in your classroom. We recommend that you:
          </p>
          <ul className="list-disc list-inside space-y-1.5 ml-2 mt-2">
            <li>Read all generated content before sharing it with students.</li>
            <li>Verify that standards references and academic content are accurate.</li>
            <li>Ensure generated assessments align with your curriculum and grading policies.</li>
            <li>Edit or discard any generated content that is not appropriate for your students.</li>
          </ul>
          <p className="mt-2">
            TeachBud is a tool to assist teachers, not to replace professional educator judgment. You bear
            responsibility for the materials distributed to students under your name.
          </p>
        </Section>

        <Section title="5. Beta Period and Service Availability">
          <p>
            TeachBud is currently in private beta. During the beta period:
          </p>
          <ul className="list-disc list-inside space-y-1.5 ml-2 mt-2">
            <li>The Service is provided free of charge to invited beta participants.</li>
            <li>Features may change, be added, or be removed without notice.</li>
            <li>We may need to take the Service offline for maintenance or updates.</li>
            <li>We may end the beta period and transition to paid plans at any time with reasonable notice.</li>
          </ul>
          <p className="mt-2 text-slate-500 italic">
            [Subscription pricing, trial terms, and payment processing terms to be added before public launch.
            Attorney review required.]
          </p>
        </Section>

        <Section title="6. Cancellation and Termination">
          <p>
            You may stop using the Service at any time. To delete your account and all associated data, contact us
            at <a href="mailto:support@teachbud.app" className="text-primary underline hover:no-underline">support@teachbud.app</a>.
          </p>
          <p className="mt-2">
            We reserve the right to suspend or terminate your account, with or without notice, if you violate these Terms,
            if we detect abuse, or if we discontinue the Service. Upon termination, your access to the Service and your
            stored data will end. We will honor data-deletion requests per our Privacy Policy.
          </p>
        </Section>

        <Section title="7. Disclaimer of Warranties">
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
            INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
            NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE, UNINTERRUPTED, OR THAT
            AI-GENERATED CONTENT WILL BE ACCURATE OR COMPLETE.
          </p>
        </Section>

        <Section title="8. Limitation of Liability">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, TEACHBUD AND ITS AFFILIATES, OFFICERS, EMPLOYEES,
            AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
            INCLUDING LOSS OF DATA, LOSS OF REVENUE, OR HARM ARISING FROM YOUR USE OF THE SERVICE OR ANY AI-GENERATED
            CONTENT, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p className="mt-2">
            OUR TOTAL LIABILITY TO YOU FOR ANY CLAIM ARISING FROM THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE
            AMOUNT YOU PAID TO US IN THE TWELVE MONTHS PRECEDING THE CLAIM, OR $100, WHICHEVER IS GREATER.
          </p>
          <p className="mt-2 text-slate-500 italic">
            [Governing-law jurisdiction and dispute-resolution provisions to be added before public launch.
            Attorney review required.]
          </p>
        </Section>

        <Section title="9. Governing Law">
          <p className="text-slate-500 italic">
            [Governing law, jurisdiction, and dispute resolution (e.g., arbitration clause) to be specified
            by legal counsel before public launch. Attorney review required.]
          </p>
        </Section>

        <Section title="10. Changes to These Terms">
          <p>
            We may update these Terms from time to time. When we do, we will update the "Last updated" date and,
            for material changes, notify you by email before the changes take effect. Continued use of the Service
            after changes take effect constitutes your acceptance of the updated Terms.
          </p>
        </Section>

        <Section title="11. Contact">
          <p>
            For questions about these Terms, contact us at:{' '}
            <a href="mailto:support@teachbud.app" className="text-primary underline hover:no-underline">support@teachbud.app</a>
          </p>
        </Section>

        <div className="pt-4 border-t border-slate-100 text-xs text-slate-400">
          <p>TeachBud · Private Beta · These terms are a draft pending attorney review.</p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-base font-bold text-slate-900 mb-3">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-3">
      <h3 className="text-sm font-semibold text-slate-800 mb-1.5">{title}</h3>
      {children}
    </div>
  );
}
